import {
  Leg,
  Stop,
  StopContext,
  stopsAreSameStop,
} from "@vygruppen/vy-train-map";

export interface Stretch {
  id: number;
  name: string;
  from: string;
  to: string;
  stops: Stop[];
  legs: Leg[];
}

export const shouldFlipStretchDirection = (
  alternativeStretchLegs: Leg[],
  selectedStops: Stop[],
) => {
  if (alternativeStretchLegs.length < 2 || selectedStops.length < 2)
    return false;
  const { fromStop: fromStopAlternative } = alternativeStretchLegs[0];
  const { toStop: toStopAlternative } =
    alternativeStretchLegs[alternativeStretchLegs.length - 1];
  const fromStop = selectedStops[0];
  const toStop = selectedStops[selectedStops.length - 1];
  return (
    stopsAreSameStop(fromStop, toStopAlternative) ||
    stopsAreSameStop(toStop, fromStopAlternative)
  );
};

const idforStop = (stop: Stop) =>
  stop.stopIds.map(({ context, id }) => `${context}.${id}`).join("-");

export const orderLegs = (legs: Leg[]) => {
  // Create a map to track connections between legs.
  const fromMap: Map<string, Stop> = new Map();
  const toMap: Map<string, Stop> = new Map();

  legs.forEach(({ fromStop, toStop }) => {
    fromMap.set(idforStop(fromStop), toStop);
    toMap.set(idforStop(toStop), fromStop);
  });

  // Identify the starting leg which does not appear as a "toStop".
  let start = null;

  for (let i = 0; i < legs.length; i += 1) {
    if (!toMap.get(idforStop(legs[i].fromStop))) {
      start = legs[i].fromStop;
      break;
    }
  }

  // Order the nodes starting from the identified start.
  const orderedLegs = [];
  let current = start;
  while (current) {
    const next = fromMap.get(idforStop(current));
    if (next) {
      orderedLegs.push({ fromStop: current, toStop: next });
      current = next;
    } else {
      current = null;
    }
  }

  return orderedLegs;
};

export const flipLegDirections = (legs: Leg[]): Leg[] =>
  legs.reverse().map((leg) => ({
    fromStop: leg.toStop,
    toStop: leg.fromStop,
  }));

export const formatStretchName = (stretch: Stretch, flipDirection: boolean) =>
  `${stretch.name} (${flipDirection ? stretch.to : stretch.from} - ${
    flipDirection ? stretch.from : stretch.to
  })`;

export const ALTERNATIVE_STRETCHES: Stretch[] = [
  {
    id: 0,
    name: "Gardermobanen",
    from: "Oslo S",
    to: "Lillestrøm",
    stops: [
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
      {
        name: "Hellerud",
        countryCode: "NO",
        stopIds: [{ id: "HLR", context: StopContext.JBV }],
      },
      {
        name: "Lillestrøm",
        countryCode: "NO",
        stopIds: [{ id: "LLS", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hellerud",
          countryCode: "NO",
          stopIds: [{ id: "HLR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hellerud",
          countryCode: "NO",
          stopIds: [{ id: "HLR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lillestrøm",
          countryCode: "NO",
          stopIds: [{ id: "LLS", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 1,
    name: "Hovedbanen",
    from: "Oslo S",
    to: "Lillestrøm",
    stops: [
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
      {
        name: "Bryn",
        countryCode: "NO",
        stopIds: [{ id: "BR", context: StopContext.JBV }],
      },
      {
        name: "Brobekk",
        countryCode: "NO",
        stopIds: [{ id: "BRB", context: StopContext.JBV }],
      },
      {
        name: "Alna",
        countryCode: "NO",
        stopIds: [{ id: "ALA", context: StopContext.JBV }],
      },
      {
        name: "Aker",
        countryCode: "NO",
        stopIds: [{ id: "AKE", context: StopContext.JBV }],
      },
      {
        name: "Nyland",
        countryCode: "NO",
        stopIds: [{ id: "NYL", context: StopContext.JBV }],
      },
      {
        name: "Grorud",
        countryCode: "NO",
        stopIds: [{ id: "GRO", context: StopContext.JBV }],
      },
      {
        name: "Haugenstua",
        countryCode: "NO",
        stopIds: [{ id: "HGA", context: StopContext.JBV }],
      },
      {
        name: "Høybråten",
        countryCode: "NO",
        stopIds: [{ id: "HØB", context: StopContext.JBV }],
      },
      {
        name: "Lørenskog",
        countryCode: "NO",
        stopIds: [{ id: "LØR", context: StopContext.JBV }],
      },
      {
        name: "Hanaborg",
        countryCode: "NO",
        stopIds: [{ id: "HAB", context: StopContext.JBV }],
      },
      {
        name: "Fjellhamar",
        countryCode: "NO",
        stopIds: [{ id: "FJE", context: StopContext.JBV }],
      },
      {
        name: "Strømmen",
        countryCode: "NO",
        stopIds: [{ id: "STN", context: StopContext.JBV }],
      },
      {
        name: "Sagdalen",
        countryCode: "NO",
        stopIds: [{ id: "SDA", context: StopContext.JBV }],
      },
      {
        name: "Lillestrøm",
        countryCode: "NO",
        stopIds: [{ id: "LLS", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Bryn",
          countryCode: "NO",
          stopIds: [{ id: "BR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Bryn",
          countryCode: "NO",
          stopIds: [{ id: "BR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Brobekk",
          countryCode: "NO",
          stopIds: [{ id: "BRB", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Brobekk",
          countryCode: "NO",
          stopIds: [{ id: "BRB", context: StopContext.JBV }],
        },
        toStop: {
          name: "Alna",
          countryCode: "NO",
          stopIds: [{ id: "ALA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Alna",
          countryCode: "NO",
          stopIds: [{ id: "ALA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Aker",
          countryCode: "NO",
          stopIds: [{ id: "AKE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Aker",
          countryCode: "NO",
          stopIds: [{ id: "AKE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nyland",
          countryCode: "NO",
          stopIds: [{ id: "NYL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nyland",
          countryCode: "NO",
          stopIds: [{ id: "NYL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Grorud",
          countryCode: "NO",
          stopIds: [{ id: "GRO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Grorud",
          countryCode: "NO",
          stopIds: [{ id: "GRO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Haugenstua",
          countryCode: "NO",
          stopIds: [{ id: "HGA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Haugenstua",
          countryCode: "NO",
          stopIds: [{ id: "HGA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Høybråten",
          countryCode: "NO",
          stopIds: [{ id: "HØB", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Høybråten",
          countryCode: "NO",
          stopIds: [{ id: "HØB", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lørenskog",
          countryCode: "NO",
          stopIds: [{ id: "LØR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Lørenskog",
          countryCode: "NO",
          stopIds: [{ id: "LØR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hanaborg",
          countryCode: "NO",
          stopIds: [{ id: "HAB", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hanaborg",
          countryCode: "NO",
          stopIds: [{ id: "HAB", context: StopContext.JBV }],
        },
        toStop: {
          name: "Fjellhamar",
          countryCode: "NO",
          stopIds: [{ id: "FJE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Fjellhamar",
          countryCode: "NO",
          stopIds: [{ id: "FJE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Strømmen",
          countryCode: "NO",
          stopIds: [{ id: "STN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Strømmen",
          countryCode: "NO",
          stopIds: [{ id: "STN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sagdalen",
          countryCode: "NO",
          stopIds: [{ id: "SDA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sagdalen",
          countryCode: "NO",
          stopIds: [{ id: "SDA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lillestrøm",
          countryCode: "NO",
          stopIds: [{ id: "LLS", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 2,
    name: "Østfoldbanen",
    from: "Oslo S",
    to: "Ski",
    stops: [
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
      {
        name: "Bekkelaget",
        countryCode: "NO",
        stopIds: [{ id: "BEK", context: StopContext.JBV }],
      },
      {
        name: "Nordstrand",
        countryCode: "NO",
        stopIds: [{ id: "NST", context: StopContext.JBV }],
      },
      {
        name: "Ljan",
        countryCode: "NO",
        stopIds: [{ id: "LJA", context: StopContext.JBV }],
      },
      {
        name: "Hauketo",
        countryCode: "NO",
        stopIds: [{ id: "HTO", context: StopContext.JBV }],
      },
      {
        name: "Holmlia",
        countryCode: "NO",
        stopIds: [{ id: "HMA", context: StopContext.JBV }],
      },
      {
        name: "Rosenholm",
        countryCode: "NO",
        stopIds: [{ id: "RSH", context: StopContext.JBV }],
      },
      {
        name: "Kolbotn",
        countryCode: "NO",
        stopIds: [{ id: "KOL", context: StopContext.JBV }],
      },
      {
        name: "Solbråtan",
        countryCode: "NO",
        stopIds: [{ id: "SOL", context: StopContext.JBV }],
      },
      {
        name: "Myrvoll",
        countryCode: "NO",
        stopIds: [{ id: "MYV", context: StopContext.JBV }],
      },
      {
        name: "Greverud",
        countryCode: "NO",
        stopIds: [{ id: "GUD", context: StopContext.JBV }],
      },
      {
        name: "Oppegård",
        countryCode: "NO",
        stopIds: [{ id: "OPG", context: StopContext.JBV }],
      },
      {
        name: "Vevelstad",
        countryCode: "NO",
        stopIds: [{ id: "VEV", context: StopContext.JBV }],
      },
      {
        name: "Langhus",
        countryCode: "NO",
        stopIds: [{ id: "LAN", context: StopContext.JBV }],
      },
      {
        name: "Ski",
        countryCode: "NO",
        stopIds: [{ id: "SKI", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Bekkelaget",
          countryCode: "NO",
          stopIds: [{ id: "BEK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Bekkelaget",
          countryCode: "NO",
          stopIds: [{ id: "BEK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nordstrand",
          countryCode: "NO",
          stopIds: [{ id: "NST", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nordstrand",
          countryCode: "NO",
          stopIds: [{ id: "NST", context: StopContext.JBV }],
        },
        toStop: {
          name: "Ljan",
          countryCode: "NO",
          stopIds: [{ id: "LJA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Ljan",
          countryCode: "NO",
          stopIds: [{ id: "LJA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hauketo",
          countryCode: "NO",
          stopIds: [{ id: "HTO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hauketo",
          countryCode: "NO",
          stopIds: [{ id: "HTO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Holmlia",
          countryCode: "NO",
          stopIds: [{ id: "HMA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Holmlia",
          countryCode: "NO",
          stopIds: [{ id: "HMA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Rosenholm",
          countryCode: "NO",
          stopIds: [{ id: "RSH", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Rosenholm",
          countryCode: "NO",
          stopIds: [{ id: "RSH", context: StopContext.JBV }],
        },
        toStop: {
          name: "Kolbotn",
          countryCode: "NO",
          stopIds: [{ id: "KOL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Kolbotn",
          countryCode: "NO",
          stopIds: [{ id: "KOL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Solbråtan",
          countryCode: "NO",
          stopIds: [{ id: "SOL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Solbråtan",
          countryCode: "NO",
          stopIds: [{ id: "SOL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Myrvoll",
          countryCode: "NO",
          stopIds: [{ id: "MYV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Myrvoll",
          countryCode: "NO",
          stopIds: [{ id: "MYV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Greverud",
          countryCode: "NO",
          stopIds: [{ id: "GUD", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Greverud",
          countryCode: "NO",
          stopIds: [{ id: "GUD", context: StopContext.JBV }],
        },
        toStop: {
          name: "Oppegård",
          countryCode: "NO",
          stopIds: [{ id: "OPG", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Oppegård",
          countryCode: "NO",
          stopIds: [{ id: "OPG", context: StopContext.JBV }],
        },
        toStop: {
          name: "Vevelstad",
          countryCode: "NO",
          stopIds: [{ id: "VEV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Vevelstad",
          countryCode: "NO",
          stopIds: [{ id: "VEV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Langhus",
          countryCode: "NO",
          stopIds: [{ id: "LAN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Langhus",
          countryCode: "NO",
          stopIds: [{ id: "LAN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Ski",
          countryCode: "NO",
          stopIds: [{ id: "SKI", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 3,
    name: "Follobanen (tunnelen)",
    from: "Oslo S",
    to: "Ski",
    stops: [
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
      {
        name: "Ski",
        countryCode: "NO",
        stopIds: [{ id: "SKI", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Ski",
          countryCode: "NO",
          stopIds: [{ id: "SKI", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 4,
    name: "Drammenbanen",
    from: "Lysaker",
    to: "Sandvika",
    stops: [
      {
        name: "Lysaker",
        countryCode: "NO",
        stopIds: [{ id: "LYS", context: StopContext.JBV }],
      },
      {
        name: "Stabekk",
        countryCode: "NO",
        stopIds: [{ id: "STB", context: StopContext.JBV }],
      },
      {
        name: "Høvik",
        countryCode: "NO",
        stopIds: [{ id: "HVK", context: StopContext.JBV }],
      },
      {
        name: "Blommenholm",
        countryCode: "NO",
        stopIds: [{ id: "BLO", context: StopContext.JBV }],
      },
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Stabekk",
          countryCode: "NO",
          stopIds: [{ id: "STB", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Stabekk",
          countryCode: "NO",
          stopIds: [{ id: "STB", context: StopContext.JBV }],
        },
        toStop: {
          name: "Høvik",
          countryCode: "NO",
          stopIds: [{ id: "HVK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Høvik",
          countryCode: "NO",
          stopIds: [{ id: "HVK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Blommenholm",
          countryCode: "NO",
          stopIds: [{ id: "BLO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Blommenholm",
          countryCode: "NO",
          stopIds: [{ id: "BLO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 5,
    name: "Askerbanen",
    from: "Lysaker",
    to: "Sandvika",
    stops: [
      {
        name: "Lysaker",
        countryCode: "NO",
        stopIds: [{ id: "LYS", context: StopContext.JBV }],
      },
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 6,
    name: "Drammenbanen",
    from: "Sandvika",
    to: "Asker",
    stops: [
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
      {
        name: "Slependen",
        countryCode: "NO",
        stopIds: [{ id: "SLE", context: StopContext.JBV }],
      },
      {
        name: "Billingstad",
        countryCode: "NO",
        stopIds: [{ id: "BST", context: StopContext.JBV }],
      },
      {
        name: "Hvalstad",
        countryCode: "NO",
        stopIds: [{ id: "HVA", context: StopContext.JBV }],
      },
      {
        name: "Vakås",
        countryCode: "NO",
        stopIds: [{ id: "VAK", context: StopContext.JBV }],
      },
      {
        name: "Høn",
        countryCode: "NO",
        stopIds: [{ id: "HØN", context: StopContext.JBV }],
      },
      {
        name: "Asker",
        countryCode: "NO",
        stopIds: [{ id: "ASR", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Slependen",
          countryCode: "NO",
          stopIds: [{ id: "SLE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Slependen",
          countryCode: "NO",
          stopIds: [{ id: "SLE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Billingstad",
          countryCode: "NO",
          stopIds: [{ id: "BST", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Billingstad",
          countryCode: "NO",
          stopIds: [{ id: "BST", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hvalstad",
          countryCode: "NO",
          stopIds: [{ id: "HVA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hvalstad",
          countryCode: "NO",
          stopIds: [{ id: "HVA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Vakås",
          countryCode: "NO",
          stopIds: [{ id: "VAK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Vakås",
          countryCode: "NO",
          stopIds: [{ id: "VAK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Høn",
          countryCode: "NO",
          stopIds: [{ id: "HØN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Høn",
          countryCode: "NO",
          stopIds: [{ id: "HØN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 7,
    name: "Askerbanen",
    from: "Sandvika",
    to: "Asker",
    stops: [
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
      {
        name: "Asker",
        countryCode: "NO",
        stopIds: [{ id: "ASR", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 8,
    name: "Drammenbanen",
    from: "Lysaker",
    to: "Asker",
    stops: [
      {
        name: "Lysaker",
        countryCode: "NO",
        stopIds: [{ id: "LYS", context: StopContext.JBV }],
      },
      {
        name: "Stabekk",
        countryCode: "NO",
        stopIds: [{ id: "STB", context: StopContext.JBV }],
      },
      {
        name: "Høvik",
        countryCode: "NO",
        stopIds: [{ id: "HVK", context: StopContext.JBV }],
      },
      {
        name: "Blommenholm",
        countryCode: "NO",
        stopIds: [{ id: "BLO", context: StopContext.JBV }],
      },
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
      {
        name: "Slependen",
        countryCode: "NO",
        stopIds: [{ id: "SLE", context: StopContext.JBV }],
      },
      {
        name: "Billingstad",
        countryCode: "NO",
        stopIds: [{ id: "BST", context: StopContext.JBV }],
      },
      {
        name: "Hvalstad",
        countryCode: "NO",
        stopIds: [{ id: "HVA", context: StopContext.JBV }],
      },
      {
        name: "Vakås",
        countryCode: "NO",
        stopIds: [{ id: "VAK", context: StopContext.JBV }],
      },
      {
        name: "Høn",
        countryCode: "NO",
        stopIds: [{ id: "HØN", context: StopContext.JBV }],
      },
      {
        name: "Asker",
        countryCode: "NO",
        stopIds: [{ id: "ASR", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Stabekk",
          countryCode: "NO",
          stopIds: [{ id: "STB", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Stabekk",
          countryCode: "NO",
          stopIds: [{ id: "STB", context: StopContext.JBV }],
        },
        toStop: {
          name: "Høvik",
          countryCode: "NO",
          stopIds: [{ id: "HVK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Høvik",
          countryCode: "NO",
          stopIds: [{ id: "HVK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Blommenholm",
          countryCode: "NO",
          stopIds: [{ id: "BLO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Blommenholm",
          countryCode: "NO",
          stopIds: [{ id: "BLO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Slependen",
          countryCode: "NO",
          stopIds: [{ id: "SLE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Slependen",
          countryCode: "NO",
          stopIds: [{ id: "SLE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Billingstad",
          countryCode: "NO",
          stopIds: [{ id: "BST", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Billingstad",
          countryCode: "NO",
          stopIds: [{ id: "BST", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hvalstad",
          countryCode: "NO",
          stopIds: [{ id: "HVA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hvalstad",
          countryCode: "NO",
          stopIds: [{ id: "HVA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Vakås",
          countryCode: "NO",
          stopIds: [{ id: "VAK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Vakås",
          countryCode: "NO",
          stopIds: [{ id: "VAK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Høn",
          countryCode: "NO",
          stopIds: [{ id: "HØN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Høn",
          countryCode: "NO",
          stopIds: [{ id: "HØN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 9,
    name: "Askerbanen",
    from: "Lysaker",
    to: "Asker",
    stops: [
      {
        name: "Lysaker",
        countryCode: "NO",
        stopIds: [{ id: "LYS", context: StopContext.JBV }],
      },
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
      {
        name: "Asker",
        countryCode: "NO",
        stopIds: [{ id: "ASR", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 10,
    name: "Hovedbanen",
    from: "Lillestrøm",
    to: "Kløfta",
    stops: [
      {
        name: "Lillestrøm",
        countryCode: "NO",
        stopIds: [{ id: "LLS", context: StopContext.JBV }],
      },
      {
        name: "Leirsund",
        countryCode: "NO",
        stopIds: [{ id: "LSD", context: StopContext.JBV }],
      },
      {
        name: "Frogner",
        countryCode: "NO",
        stopIds: [{ id: "FRO", context: StopContext.JBV }],
      },
      {
        name: "Lindeberg",
        countryCode: "NO",
        stopIds: [{ id: "LBG", context: StopContext.JBV }],
      },
      {
        name: "Kløfta",
        countryCode: "NO",
        stopIds: [{ id: "KLØ", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lillestrøm",
          countryCode: "NO",
          stopIds: [{ id: "LLS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Leirsund",
          countryCode: "NO",
          stopIds: [{ id: "LSD", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Leirsund",
          countryCode: "NO",
          stopIds: [{ id: "LSD", context: StopContext.JBV }],
        },
        toStop: {
          name: "Frogner",
          countryCode: "NO",
          stopIds: [{ id: "FRO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Frogner",
          countryCode: "NO",
          stopIds: [{ id: "FRO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lindeberg",
          countryCode: "NO",
          stopIds: [{ id: "LBG", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Lindeberg",
          countryCode: "NO",
          stopIds: [{ id: "LBG", context: StopContext.JBV }],
        },
        toStop: {
          name: "Kløfta",
          countryCode: "NO",
          stopIds: [{ id: "KLØ", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 11,
    name: "Gardermobanen",
    from: "Lillestrøm",
    to: "Kløfta",
    stops: [
      {
        name: "Lillestrøm",
        countryCode: "NO",
        stopIds: [{ id: "LLS", context: StopContext.JBV }],
      },
      {
        name: "Kløfta",
        countryCode: "NO",
        stopIds: [{ id: "KLØ", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Lillestrøm",
          countryCode: "NO",
          stopIds: [{ id: "LLS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Kløfta",
          countryCode: "NO",
          stopIds: [{ id: "KLØ", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 12,
    name: "Hovedbanen",
    from: "Langeland",
    to: "Eidsvoll",
    stops: [
      {
        name: "Langeland",
        countryCode: "NO",
        stopIds: [{ id: "LAL", context: StopContext.JBV }],
      },
      {
        name: "Jessheim",
        countryCode: "NO",
        stopIds: [{ id: "JEH", context: StopContext.JBV }],
      },
      {
        name: "Nordby",
        countryCode: "NO",
        stopIds: [{ id: "NBY", context: StopContext.JBV }],
      },
      {
        name: "Hauerseter",
        countryCode: "NO",
        stopIds: [{ id: "HSR", context: StopContext.JBV }],
      },
      {
        name: "Sand",
        countryCode: "NO",
        stopIds: [{ id: "SAD", context: StopContext.JBV }],
      },
      {
        name: "Dal",
        countryCode: "NO",
        stopIds: [{ id: "DAL", context: StopContext.JBV }],
      },
      {
        name: "Bøn",
        countryCode: "NO",
        stopIds: [{ id: "BØN", context: StopContext.JBV }],
      },
      {
        name: "Eidsvoll",
        countryCode: "NO",
        stopIds: [{ id: "EVL", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Langeland",
          countryCode: "NO",
          stopIds: [{ id: "LAL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Jessheim",
          countryCode: "NO",
          stopIds: [{ id: "JEH", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Jessheim",
          countryCode: "NO",
          stopIds: [{ id: "JEH", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nordby",
          countryCode: "NO",
          stopIds: [{ id: "NBY", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nordby",
          countryCode: "NO",
          stopIds: [{ id: "NBY", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hauerseter",
          countryCode: "NO",
          stopIds: [{ id: "HSR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hauerseter",
          countryCode: "NO",
          stopIds: [{ id: "HSR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sand",
          countryCode: "NO",
          stopIds: [{ id: "SAD", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sand",
          countryCode: "NO",
          stopIds: [{ id: "SAD", context: StopContext.JBV }],
        },
        toStop: {
          name: "Dal",
          countryCode: "NO",
          stopIds: [{ id: "DAL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Dal",
          countryCode: "NO",
          stopIds: [{ id: "DAL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Bøn",
          countryCode: "NO",
          stopIds: [{ id: "BØN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Bøn",
          countryCode: "NO",
          stopIds: [{ id: "BØN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Eidsvoll",
          countryCode: "NO",
          stopIds: [{ id: "EVL", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 13,
    name: "Gardermobanen",
    from: "Langeland",
    to: "Eidsvoll",
    stops: [
      {
        name: "Eidsvoll",
        countryCode: "NO",
        stopIds: [{ id: "EVL", context: StopContext.JBV }],
      },
      {
        name: "Eidsvoll Verk",
        countryCode: "NO",
        stopIds: [{ id: "EVV", context: StopContext.JBV }],
      },
      {
        name: "Bekkedalshøgda",
        countryCode: "NO",
        stopIds: [{ id: "BKH", context: StopContext.JBV }],
      },
      {
        name: "Gardermoen",
        countryCode: "NO",
        stopIds: [{ id: "GAR", context: StopContext.JBV }],
      },
      {
        name: "Langeland",
        countryCode: "NO",
        stopIds: [{ id: "LAL", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Eidsvoll",
          countryCode: "NO",
          stopIds: [{ id: "EVL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Eidsvoll Verk",
          countryCode: "NO",
          stopIds: [{ id: "EVV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Eidsvoll Verk",
          countryCode: "NO",
          stopIds: [{ id: "EVV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Bekkedalshøgda",
          countryCode: "NO",
          stopIds: [{ id: "BKH", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Bekkedalshøgda",
          countryCode: "NO",
          stopIds: [{ id: "BKH", context: StopContext.JBV }],
        },
        toStop: {
          name: "Gardermoen",
          countryCode: "NO",
          stopIds: [{ id: "GAR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Gardermoen",
          countryCode: "NO",
          stopIds: [{ id: "GAR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Langeland",
          countryCode: "NO",
          stopIds: [{ id: "LAL", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 14,
    name: "Via Drammen",
    from: "Hønefoss",
    to: "Oslo S",
    stops: [
      {
        name: "Hønefoss",
        countryCode: "NO",
        stopIds: [{ id: "HFS", context: StopContext.JBV }],
      },
      {
        name: "Tyristrand",
        countryCode: "NO",
        stopIds: [{ id: "TYR", context: StopContext.JBV }],
      },
      {
        name: "Vikersund",
        countryCode: "NO",
        stopIds: [{ id: "VKS", context: StopContext.JBV }],
      },
      {
        name: "Geithus",
        countryCode: "NO",
        stopIds: [{ id: "GHS", context: StopContext.JBV }],
      },
      {
        name: "Åmot",
        countryCode: "NO",
        stopIds: [{ id: "ÅMO", context: StopContext.JBV }],
      },
      {
        name: "Skotselv",
        countryCode: "NO",
        stopIds: [{ id: "SEV", context: StopContext.JBV }],
      },
      {
        name: "Hokksund",
        countryCode: "NO",
        stopIds: [{ id: "HOK", context: StopContext.JBV }],
      },
      {
        name: "Steinberg",
        countryCode: "NO",
        stopIds: [{ id: "SBG", context: StopContext.JBV }],
      },
      {
        name: "Mjøndalen",
        countryCode: "NO",
        stopIds: [{ id: "MJD", context: StopContext.JBV }],
      },
      {
        name: "Daler",
        countryCode: "NO",
        stopIds: [{ id: "DLR", context: StopContext.JBV }],
      },
      {
        name: "Gulskogen",
        countryCode: "NO",
        stopIds: [{ id: "GUL", context: StopContext.JBV }],
      },
      {
        name: "Drammen",
        countryCode: "NO",
        stopIds: [{ id: "DRM", context: StopContext.JBV }],
      },
      {
        name: "Brakerøya",
        countryCode: "NO",
        stopIds: [{ id: "BRA", context: StopContext.JBV }],
      },
      {
        name: "Lier",
        countryCode: "NO",
        stopIds: [{ id: "LIE", context: StopContext.JBV }],
      },
      {
        name: "Eriksrud",
        countryCode: "NO",
        stopIds: [{ id: "ERU", context: StopContext.JBV }],
      },
      {
        name: "Asker",
        countryCode: "NO",
        stopIds: [{ id: "ASR", context: StopContext.JBV }],
      },
      {
        name: "Sandvika",
        countryCode: "NO",
        stopIds: [{ id: "SV", context: StopContext.JBV }],
      },
      {
        name: "Lysaker",
        countryCode: "NO",
        stopIds: [{ id: "LYS", context: StopContext.JBV }],
      },
      {
        name: "Skøyen",
        countryCode: "NO",
        stopIds: [{ id: "SKØ", context: StopContext.JBV }],
      },
      {
        name: "Nationaltheatret",
        countryCode: "NO",
        stopIds: [{ id: "NTH", context: StopContext.JBV }],
      },
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Hønefoss",
          countryCode: "NO",
          stopIds: [{ id: "HFS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Tyristrand",
          countryCode: "NO",
          stopIds: [{ id: "TYR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Tyristrand",
          countryCode: "NO",
          stopIds: [{ id: "TYR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Vikersund",
          countryCode: "NO",
          stopIds: [{ id: "VKS", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Vikersund",
          countryCode: "NO",
          stopIds: [{ id: "VKS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Geithus",
          countryCode: "NO",
          stopIds: [{ id: "GHS", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Geithus",
          countryCode: "NO",
          stopIds: [{ id: "GHS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Åmot",
          countryCode: "NO",
          stopIds: [{ id: "ÅMO", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Åmot",
          countryCode: "NO",
          stopIds: [{ id: "ÅMO", context: StopContext.JBV }],
        },
        toStop: {
          name: "Skotselv",
          countryCode: "NO",
          stopIds: [{ id: "SEV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Skotselv",
          countryCode: "NO",
          stopIds: [{ id: "SEV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hokksund",
          countryCode: "NO",
          stopIds: [{ id: "HOK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hokksund",
          countryCode: "NO",
          stopIds: [{ id: "HOK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Steinberg",
          countryCode: "NO",
          stopIds: [{ id: "SBG", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Steinberg",
          countryCode: "NO",
          stopIds: [{ id: "SBG", context: StopContext.JBV }],
        },
        toStop: {
          name: "Mjøndalen",
          countryCode: "NO",
          stopIds: [{ id: "MJD", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Mjøndalen",
          countryCode: "NO",
          stopIds: [{ id: "MJD", context: StopContext.JBV }],
        },
        toStop: {
          name: "Daler",
          countryCode: "NO",
          stopIds: [{ id: "DLR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Daler",
          countryCode: "NO",
          stopIds: [{ id: "DLR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Gulskogen",
          countryCode: "NO",
          stopIds: [{ id: "GUL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Gulskogen",
          countryCode: "NO",
          stopIds: [{ id: "GUL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Drammen",
          countryCode: "NO",
          stopIds: [{ id: "DRM", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Drammen",
          countryCode: "NO",
          stopIds: [{ id: "DRM", context: StopContext.JBV }],
        },
        toStop: {
          name: "Brakerøya",
          countryCode: "NO",
          stopIds: [{ id: "BRA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Brakerøya",
          countryCode: "NO",
          stopIds: [{ id: "BRA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lier",
          countryCode: "NO",
          stopIds: [{ id: "LIE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Lier",
          countryCode: "NO",
          stopIds: [{ id: "LIE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Eriksrud",
          countryCode: "NO",
          stopIds: [{ id: "ERU", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Eriksrud",
          countryCode: "NO",
          stopIds: [{ id: "ERU", context: StopContext.JBV }],
        },
        toStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Asker",
          countryCode: "NO",
          stopIds: [{ id: "ASR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sandvika",
          countryCode: "NO",
          stopIds: [{ id: "SV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Lysaker",
          countryCode: "NO",
          stopIds: [{ id: "LYS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Skøyen",
          countryCode: "NO",
          stopIds: [{ id: "SKØ", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Skøyen",
          countryCode: "NO",
          stopIds: [{ id: "SKØ", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nationaltheatret",
          countryCode: "NO",
          stopIds: [{ id: "NTH", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nationaltheatret",
          countryCode: "NO",
          stopIds: [{ id: "NTH", context: StopContext.JBV }],
        },
        toStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
      },
    ],
  },
  {
    id: 15,
    name: "Via Roa",
    from: "Hønefoss",
    to: "Oslo S",
    stops: [
      {
        name: "Hønefoss",
        countryCode: "NO",
        stopIds: [{ id: "HFS", context: StopContext.JBV }],
      },
      {
        name: "Hval",
        countryCode: "NO",
        stopIds: [{ id: "HV", context: StopContext.JBV }],
      },
      {
        name: "Jevnaker",
        countryCode: "NO",
        stopIds: [{ id: "JEV", context: StopContext.JBV }],
      },
      {
        name: "Grindvoll",
        countryCode: "NO",
        stopIds: [{ id: "GVL", context: StopContext.JBV }],
      },
      {
        name: "Roa",
        countryCode: "NO",
        stopIds: [{ id: "ROA", context: StopContext.JBV }],
      },
      {
        name: "Grua",
        countryCode: "NO",
        stopIds: [{ id: "GRU", context: StopContext.JBV }],
      },
      {
        name: "Bjørgeseter",
        countryCode: "NO",
        stopIds: [{ id: "BSR", context: StopContext.JBV }],
      },
      {
        name: "Harestua",
        countryCode: "NO",
        stopIds: [{ id: "HST", context: StopContext.JBV }],
      },
      {
        name: "Monsrud",
        countryCode: "NO",
        stopIds: [{ id: "MON", context: StopContext.JBV }],
      },
      {
        name: "Stryken",
        countryCode: "NO",
        stopIds: [{ id: "SY", context: StopContext.JBV }],
      },
      {
        name: "Jensrud",
        countryCode: "NO",
        stopIds: [{ id: "JEN", context: StopContext.JBV }],
      },
      {
        name: "Hakadal",
        countryCode: "NO",
        stopIds: [{ id: "HAK", context: StopContext.JBV }],
      },
      {
        name: "Varingskollen",
        countryCode: "NO",
        stopIds: [{ id: "VAR", context: StopContext.JBV }],
      },
      {
        name: "Åneby",
        countryCode: "NO",
        stopIds: [{ id: "ÅBY", context: StopContext.JBV }],
      },
      {
        name: "Nittedal",
        countryCode: "NO",
        stopIds: [{ id: "NIT", context: StopContext.JBV }],
      },
      {
        name: "Movatn",
        countryCode: "NO",
        stopIds: [{ id: "MVT", context: StopContext.JBV }],
      },
      {
        name: "Snippen",
        countryCode: "NO",
        stopIds: [{ id: "SNI", context: StopContext.JBV }],
      },
      {
        name: "Sandermosen",
        countryCode: "NO",
        stopIds: [{ id: "SDM", context: StopContext.JBV }],
      },
      {
        name: "Kjelsås",
        countryCode: "NO",
        stopIds: [{ id: "KJE", context: StopContext.JBV }],
      },
      {
        name: "Nydalen",
        countryCode: "NO",
        stopIds: [{ id: "NYD", context: StopContext.JBV }],
      },
      {
        name: "Grefsen",
        countryCode: "NO",
        stopIds: [{ id: "GRE", context: StopContext.JBV }],
      },
      {
        name: "Tøyen",
        countryCode: "NO",
        stopIds: [{ id: "TØY", context: StopContext.JBV }],
      },
      {
        name: "Oslo S",
        countryCode: "NO",
        stopIds: [{ id: "OSL", context: StopContext.JBV }],
      },
    ],
    legs: [
      {
        fromStop: {
          name: "Hønefoss",
          countryCode: "NO",
          stopIds: [{ id: "HFS", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hval",
          countryCode: "NO",
          stopIds: [{ id: "HV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hval",
          countryCode: "NO",
          stopIds: [{ id: "HV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Jevnaker",
          countryCode: "NO",
          stopIds: [{ id: "JEV", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Jevnaker",
          countryCode: "NO",
          stopIds: [{ id: "JEV", context: StopContext.JBV }],
        },
        toStop: {
          name: "Grindvoll",
          countryCode: "NO",
          stopIds: [{ id: "GVL", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Grindvoll",
          countryCode: "NO",
          stopIds: [{ id: "GVL", context: StopContext.JBV }],
        },
        toStop: {
          name: "Roa",
          countryCode: "NO",
          stopIds: [{ id: "ROA", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Roa",
          countryCode: "NO",
          stopIds: [{ id: "ROA", context: StopContext.JBV }],
        },
        toStop: {
          name: "Grua",
          countryCode: "NO",
          stopIds: [{ id: "GRU", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Grua",
          countryCode: "NO",
          stopIds: [{ id: "GRU", context: StopContext.JBV }],
        },
        toStop: {
          name: "Bjørgeseter",
          countryCode: "NO",
          stopIds: [{ id: "BSR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Bjørgeseter",
          countryCode: "NO",
          stopIds: [{ id: "BSR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Harestua",
          countryCode: "NO",
          stopIds: [{ id: "HST", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Harestua",
          countryCode: "NO",
          stopIds: [{ id: "HST", context: StopContext.JBV }],
        },
        toStop: {
          name: "Monsrud",
          countryCode: "NO",
          stopIds: [{ id: "MON", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Monsrud",
          countryCode: "NO",
          stopIds: [{ id: "MON", context: StopContext.JBV }],
        },
        toStop: {
          name: "Stryken",
          countryCode: "NO",
          stopIds: [{ id: "SY", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Stryken",
          countryCode: "NO",
          stopIds: [{ id: "SY", context: StopContext.JBV }],
        },
        toStop: {
          name: "Jensrud",
          countryCode: "NO",
          stopIds: [{ id: "JEN", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Jensrud",
          countryCode: "NO",
          stopIds: [{ id: "JEN", context: StopContext.JBV }],
        },
        toStop: {
          name: "Hakadal",
          countryCode: "NO",
          stopIds: [{ id: "HAK", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Hakadal",
          countryCode: "NO",
          stopIds: [{ id: "HAK", context: StopContext.JBV }],
        },
        toStop: {
          name: "Varingskollen",
          countryCode: "NO",
          stopIds: [{ id: "VAR", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Varingskollen",
          countryCode: "NO",
          stopIds: [{ id: "VAR", context: StopContext.JBV }],
        },
        toStop: {
          name: "Åneby",
          countryCode: "NO",
          stopIds: [{ id: "ÅBY", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Åneby",
          countryCode: "NO",
          stopIds: [{ id: "ÅBY", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nittedal",
          countryCode: "NO",
          stopIds: [{ id: "NIT", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nittedal",
          countryCode: "NO",
          stopIds: [{ id: "NIT", context: StopContext.JBV }],
        },
        toStop: {
          name: "Movatn",
          countryCode: "NO",
          stopIds: [{ id: "MVT", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Movatn",
          countryCode: "NO",
          stopIds: [{ id: "MVT", context: StopContext.JBV }],
        },
        toStop: {
          name: "Snippen",
          countryCode: "NO",
          stopIds: [{ id: "SNI", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Snippen",
          countryCode: "NO",
          stopIds: [{ id: "SNI", context: StopContext.JBV }],
        },
        toStop: {
          name: "Sandermosen",
          countryCode: "NO",
          stopIds: [{ id: "SDM", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Sandermosen",
          countryCode: "NO",
          stopIds: [{ id: "SDM", context: StopContext.JBV }],
        },
        toStop: {
          name: "Kjelsås",
          countryCode: "NO",
          stopIds: [{ id: "KJE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Kjelsås",
          countryCode: "NO",
          stopIds: [{ id: "KJE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Nydalen",
          countryCode: "NO",
          stopIds: [{ id: "NYD", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Nydalen",
          countryCode: "NO",
          stopIds: [{ id: "NYD", context: StopContext.JBV }],
        },
        toStop: {
          name: "Grefsen",
          countryCode: "NO",
          stopIds: [{ id: "GRE", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Grefsen",
          countryCode: "NO",
          stopIds: [{ id: "GRE", context: StopContext.JBV }],
        },
        toStop: {
          name: "Tøyen",
          countryCode: "NO",
          stopIds: [{ id: "TØY", context: StopContext.JBV }],
        },
      },
      {
        fromStop: {
          name: "Tøyen",
          countryCode: "NO",
          stopIds: [{ id: "TØY", context: StopContext.JBV }],
        },
        toStop: {
          name: "Oslo S",
          countryCode: "NO",
          stopIds: [{ id: "OSL", context: StopContext.JBV }],
        },
      },
    ],
  },
];
